import { NavLink, useLocation } from 'react-router-dom';
import { CSSProperties, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { useTheme } from '@/hooks/useTheme';
import { useNavGating } from '@/hooks/useNavGating';
import { LockBadge } from '@/components/gating/LockBadge';
import { Tooltip } from '@/components/ui/Tooltip';
import { useNavReleaseBadges } from '@/hooks/useReleaseSections';
import { NavCountBadge, useNavBadgeAria } from '@/components/layout/NavCountBadge';

/**
 * Rail Navigation Sidebar Component
 *
 * Material 3-style rail navigation for desktop (≥1024px)
 * - Fixed width: 88px (no collapse)
 * - White background
 * - No rounded corners (flush with edges)
 * - No shadow on container
 * - Vertical centered navigation items
 * - Active item shows label below icon
 * - Inactive items: icon only
 * - Uses HappyIcons icon font
 * - Shows different items for agency staff vs regular users
 */

interface NavItem {
  id: string;
  labelKey: string; // Translation key
  href: string;
  icon: string; // HappyIcon class name
  visible?: boolean;
  /** Fallback label text if `labelKey` isn't registered in the backend-served i18n resource yet. */
  labelDefault?: string;
}

export const RailSidebar = () => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { isAgencyTeam, carteBlanche } = useReviewerRestrictions();
  const { branding } = useTheme();
  // Plan-gated pages (Brand Check, Blog, Analytics) stay in the nav when locked: lock badge, locked page on open
  const navGate = useNavGating();
  const location = useLocation();
  const [isHovered, setIsHovered] = useState(false);
  // Release counts (approvals: waiting for your release, calendar: newly released, portfolio: staff reviewers)
  const releaseBadges = useNavReleaseBadges();
  const badgeAria = useNavBadgeAria();

  // Get variationsEnabled from current team (God Mode) or home team - no extra API call needed
  const variationsEnabled = user?.currentTeam?.variationsEnabled ?? user?.team?.variationsEnabled ?? false;

  // Use agency nav items if user is agency staff (but not when in God Mode)
  const isAgencyStaff = user?.isAgencyStaff === true && !user?.godModeSession;

  // Regular user navigation - moved inside component to use translations
  const regularNavItems: NavItem[] = useMemo(
    () => [
      {
        id: 'dashboard',
        labelKey: 'nav_dashboard',
        href: '/',
        icon: 'hm-dashboard', // Dashboard grid icon
        visible: true,
      },
      {
        id: 'calendar',
        labelKey: 'nav_calendar',
        href: '/calendar',
        icon: 'hm-calendar', // Calendar scheduling icon
        visible: true,
      },
      {
        id: 'creator',
        labelKey: 'nav_creator',
        href: '/creator',
        icon: 'hm-magic-wand', // Magic wand for AI-powered creation
        visible: true,
      },
      {
        id: 'brand-check',
        labelKey: 'nav_brand_check',
        href: '/brand-check',
        icon: 'hm-radar', // Radar for brand monitoring/scanning
        visible: true,
      },
      {
        id: 'media-library',
        labelKey: 'media_library',
        href: '/media-library',
        icon: 'hm-photo-stack', // Photo stack for media library
        visible: true,
      },
      {
        id: 'blog',
        labelKey: 'nav_blog',
        labelDefault: 'Blog',
        href: '/blog',
        icon: 'hm-blog-content', // Blog icon for WordPress publishing
        visible: true,
      },
      {
        id: 'variations',
        labelKey: 'nav_variations',
        href: '/variations',
        icon: 'hm-language-change', // Language/translate icon for content variations
        visible: true,
      },
      {
        id: 'approvals',
        labelKey: 'nav_approvals',
        href: '/approvals',
        icon: 'hm-clip-board', // Clipboard for approval board
        visible: true,
      },
      {
        id: 'analytics',
        labelKey: 'nav_analytics',
        href: '/analytics',
        icon: 'hm-graph', // Graph icon for analytics
        visible: true,
      },
    ],
    []
  );

  // Agency staff navigation - Portfolio is the primary daily surface
  const agencyNavItems: NavItem[] = useMemo(
    () => [
      {
        id: 'agency-portfolio',
        labelKey: 'nav_portfolio',
        href: '/agency/portfolio',
        icon: 'hm-board', // Board icon for portfolio view
        visible: true,
      },
      {
        id: 'agency-settings',
        labelKey: 'nav_agency_settings',
        href: '/agency/settings',
        icon: 'hm-tools', // Tools for agency configuration
        visible: true,
      },
    ],
    []
  );

  const navItems = isAgencyStaff ? agencyNavItems : regularNavItems;

  const visibleNavItems = useMemo(
    () =>
      navItems.filter((item) => {
        // Hide Approvals for non-agency teams OR when carte blanche is ON (auto-approval)
        if (item.id === 'approvals' && (!isAgencyTeam || carteBlanche)) {
          return false;
        }
        // Hide Variations unless enabled for the team
        if (item.id === 'variations' && !variationsEnabled) {
          return false;
        }
        if (navGate(item.id)?.hidden) {
          return false;
        }
        return item.visible;
      }),
    [navItems, isAgencyTeam, carteBlanche, variationsEnabled, navGate]
  );

  return (
    <aside
      className="hidden lg:flex fixed left-0 top-0 bottom-0 w-[88px] bg-white flex-col items-center py-4 border-r border-gray-200 z-sidebar"
      aria-label={t('aria_main_navigation')}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Compact icon logo at top */}
      <div className="w-full flex justify-center mb-6 px-4">
        <img
          src={branding.assets?.logoSmall || branding.logo || '/images/aa-logo-simple.png'}
          alt="Ägile Ässets"
          style={{ width: 'clamp(30px, 4.5vh, 42px)', height: 'auto' }}
          onError={(e) => {
            e.currentTarget.src = '/images/aa-logo-simple.png';
          }}
        />
      </div>

      {/* Navigation items - VERTICALLY CENTERED */}
      <nav className="flex-1 flex flex-col items-center justify-center gap-1" aria-label={t('aria_primary_navigation')}>
        {/* Whether the current route matches ANY rail nav item. When false (e.g. on
            /profile, /settings, /help, /status), no item would be "active", so we keep
            all items visible instead of hiding them all behind the hover state — otherwise
            the rail looks empty on utility pages. */}
        {(() => {
          const hasActiveNavItem = visibleNavItems.some((item) =>
            item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href)
          );
          return visibleNavItems.map((item, index) => {
          // Determine active state by matching the current pathname.
          // NavLink's `end` prop behaviour: exact match for "/" but prefix for others.
          const isActive =
            item.href === '/'
              ? location.pathname === '/'
              : location.pathname.startsWith(item.href);

          const label = t(item.labelKey, item.labelDefault ? { defaultValue: item.labelDefault } : undefined);
          const badge = releaseBadges[item.id];
          const gate = navGate(item.id);
          const isLocked = gate?.locked === true;
          const baseLabel = isLocked ? t('gating_locked_label', { feature: label }) : label;
          const linkLabel = badge ? `${baseLabel}, ${badgeAria(badge.count, badge.kind)}` : baseLabel;

          // Inactive items collapse away when sidebar is not hovered.
          // They transition from max-h-0 / opacity-0 (collapsed) to max-h-[60px] / opacity-100 (expanded).
          // The active item is always visible (opacity-100, normal height) so the nav container
          // naturally centres it via justify-center when all others are collapsed.
          // When no nav item matches the route (utility pages), hasActiveNavItem is false and
          // all items remain visible — consistent with the normal hover-expanded appearance.
          const isCollapsed = !isHovered && !isActive && hasActiveNavItem;

          // Stagger delay: 50 ms per item on enter, instant on leave.
          const delayStyle: CSSProperties = isHovered
            ? { transitionDelay: `${index * 50}ms` }
            : { transitionDelay: '0ms' };

          return (
            <div
              key={item.id}
              className={`w-full flex justify-center overflow-hidden transition-all ease-in-out ${
                isCollapsed
                  ? 'max-h-0 opacity-0 pointer-events-none'
                  : `${isActive && !isHovered ? 'max-h-[120px]' : 'max-h-[60px]'} opacity-100 pointer-events-auto`
              }`}
              style={{
                transitionDuration: isHovered ? '500ms' : '300ms',
                ...delayStyle,
              }}
            >
              <div className="flex flex-col items-center">
                <Tooltip content={label} side="right" sideOffset={10} className="pointer-events-none">
                  <NavLink
                    to={item.href}
                    end
                    className="flex items-center justify-center"
                    aria-label={linkLabel}
                    {...(isLocked && gate ? { 'data-testid': `locked-entry-${gate.feature}` } : {})}
                  >
                    <span
                      className={`relative flex items-center justify-center rounded-lg transition-colors hover:bg-gray-50 ${
                        isActive ? 'bg-gray-50' : ''
                      }`}
                      style={{ width: 'clamp(40px, 6vh, 56px)', height: 'clamp(40px, 6vh, 56px)' }}
                    >
                      <i
                        className={`hm ${item.icon} leading-none ${
                          isActive ? 'text-primary-500' : 'text-gray-500'
                        }`}
                        style={{ fontSize: 'clamp(22px, 3.5vh, 32px)' }}
                      />
                      {badge && <NavCountBadge count={badge.count} kind={badge.kind} className="right-0 top-0.5" />}
                      {isLocked && <LockBadge className="absolute right-0.5 bottom-0.5" />}
                    </span>
                  </NavLink>
                </Tooltip>
                {/* Collapse indicator - miniature icons directly under active item */}
                {isActive && (
                  <div
                    className={`flex items-center justify-center gap-1.5 px-2 mt-2 flex-wrap transition-opacity ${
                      isHovered ? 'opacity-0 pointer-events-none' : 'opacity-100'
                    }`}
                    style={{ transitionDuration: isHovered ? '200ms' : '500ms' }}
                    aria-hidden="true"
                  >
                    {visibleNavItems
                      .filter((navItem) => {
                        const navActive = navItem.href === '/'
                          ? location.pathname === '/'
                          : location.pathname.startsWith(navItem.href);
                        return !navActive;
                      })
                      .map((navItem) => (
                        <span key={navItem.id} className="relative inline-flex">
                          <i
                            className={`hm ${navItem.icon} text-gray-400 leading-none opacity-60`}
                            style={{ fontSize: '12px' }}
                          />
                          {releaseBadges[navItem.id] && (
                            <span className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-coral-600 ring-1 ring-white" />
                          )}
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          );
          });
        })()}

      </nav>

      {/* Full text logo at bottom */}
      <div className="mt-auto w-full flex justify-center px-2">
        <img
          src={branding.logo || '/images/logo.png'}
          alt="Ägile Ässets"
          style={{ maxWidth: 'clamp(52px, 7.5vh, 72px)', height: 'auto' }}
          onError={(e) => {
            e.currentTarget.src = '/images/logo.png';
          }}
        />
      </div>
    </aside>
  );
};
